// Brief 03 Phase 2 — wire developments table to Realtime with tiered invalidation.
//
// Tier A (immediate, every burst):
//   developments, devs-paginated, dev-stage-counts, duplicates, trash,
//   loose-archived, settings-hub-stats, archive, collections.
//
// Tier B (trailing 10s debounce, fires once per quiet period):
//   analytics — all ['analytics', ...] descendants via React Query prefix match.
//
// Notes:
// - Board's `devs` array is plain useState in useAppData.js (NOT React Query).
//   The existing DevelopmentsTab.js:179 subscription keeps it in sync.
//   Phase 7 will remove that and migrate Board to React Query.
// - There is no ['devDetail', id] React Query — DevDetail receives the dev
//   as a prop from parent. The ['developments'] invalidation cascades.
// - Existing subscription on `developments` in DevelopmentsTab.js:179 is
//   intentionally left in place. Duplicate subscriptions are cheap (Supabase
//   multiplexes the WebSocket; React Query invalidation is idempotent).

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useRealtimeSubscription } from './useRealtimeSubscription';

const TIER_A_KEYS = [
  ['developments'],
  ['devs-paginated'],
  ['dev-stage-counts'],
  ['duplicates'],
  ['trash'],
  ['loose-archived'],
  ['settings-hub-stats'],
  ['archive'],
  ['collections'],
];

const TIER_B_DEBOUNCE_MS = 10_000;

export function useDevelopmentsRealtime({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const tierBTimerRef = useRef(null);

  // Tier B trailing-edge debounce. Resets on every event in a burst; fires
  // once 10s after the last event lands.
  const scheduleTierB = () => {
    const wasScheduled = tierBTimerRef.current !== null;
    if (tierBTimerRef.current) clearTimeout(tierBTimerRef.current);

    if (!wasScheduled) {
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'dev.tier_b_scheduled',
      });
    }

    tierBTimerRef.current = setTimeout(() => {
      tierBTimerRef.current = null;
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'dev.tier_b_fired',
      });
      try {
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useDevelopmentsRealtime.tierB' },
        });
      }
    }, TIER_B_DEBOUNCE_MS);
  };

  // Cleanup pending Tier B on unmount so navigating away doesn't fire stale
  // invalidation against a torn-down QueryClient.
  useEffect(() => {
    return () => {
      if (tierBTimerRef.current) {
        clearTimeout(tierBTimerRef.current);
        tierBTimerRef.current = null;
      }
    };
  }, []);

  useRealtimeSubscription({
    table: 'developments',
    event: '*',
    enabled,
    onPayload: (events) => {
      // events is always an array (Phase 1 hook batches 50ms bursts).
      // Tier A: invalidate broad keys once per batch — not once per event.
      const affectedIds = events
        .map(e => e.new?.id || e.old?.id)
        .filter(Boolean);

      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'dev.tier_a_invalidated',
        data: {
          batch_size: events.length,
          affected_ids: affectedIds.slice(0, 5),
        },
      });

      try {
        TIER_A_KEYS.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useDevelopmentsRealtime.tierA' },
        });
      }

      // Tier B: schedule (or reset) the trailing-edge debounce.
      scheduleTierB();
    },
  });
}
